import {InvalidTokenError} from '@packages/utils';
import {parseCookies} from 'nookies';
import {useCallback, useEffect, useState} from 'react';
import {
  type ReportsRepositoryProps,
  deleteReportsMyClips,
  updateReportsMyClips,
} from '../../../core/repositories/clipsRepository';
import {useIsSessionExpired} from '../../../core/usecases/auth/useIsSessionExpired';

type HandleClipUpdateProps = Omit<ReportsRepositoryProps, 'cookies'> & {
  clipCount: number;
  clipped: boolean;
  isLogin: boolean;
};

export function useHandleClip({
  reportId,
  reportType,
  clipCount,
  clipped,
  isLogin,
}: HandleClipUpdateProps) {
  const [clipCountUpdated, setClipCountUpdated] = useState<number>(clipCount);
  const [isClipped, setIsClipped] = useState<boolean>(clipped);

  useEffect(() => {
    setClipCountUpdated(clipCount);
    setIsClipped(isClipped);
  }, [reportId]);

  const {updateIsSessionExpired} = useIsSessionExpired();

  const unClipReportUseCase = useCallback(
    async function unClipUseCase(reportTypeConvertToLowerCase: string) {
      if (!isLogin) {
        return {
          handleClip: () => Promise<void>,
          clipCountUpdated: clipCount,
          isClipped: clipped,
        };
      }

      const cookies = parseCookies();
      const result = await deleteReportsMyClips({
        cookies,
        reportId,
        reportType: reportTypeConvertToLowerCase,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      if (!result.ok) {
        return;
      }

      setIsClipped(false);
      setClipCountUpdated(result.value.clipsCount);
    },
    [reportId],
  );

  const updateReportUseCase = useCallback(
    async function updateUseCase(reportTypeConvertToLowerCase: string) {
      const cookies = parseCookies();
      const result = await updateReportsMyClips({
        cookies,
        reportId,
        reportType: reportTypeConvertToLowerCase,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      if (!result.ok) {
        return;
      }

      setIsClipped(true);
      setClipCountUpdated(result.value.clipsCount);
    },
    [reportId],
  );

  const handleClip = useCallback(async () => {
    if (isClipped) {
      await unClipReportUseCase(reportType);
      return;
    }

    await updateReportUseCase(reportType);
  }, [unClipReportUseCase, isClipped, reportType, updateReportUseCase]);

  return {handleClip, clipCountUpdated, isClipped};
}
